import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, BarChart2, Briefcase, CalendarDays, ChevronRight, Compass, Crown,
  ExternalLink, GraduationCap, Handshake, Home, Megaphone, Newspaper, Paperclip,
  Quote, TrendingUp, UserRound, Users,
} from 'lucide-react';
import SEO from '../components/SEO';
import {
  fetchDosenByIdentifier, fetchPengumumanUnit, fetchTracerStats,
  fetchTracerStories, fetchUnitAnggota, fetchUnitMemberProfile, formatTanggalIndonesia, getImageUrl,
} from '../utils/api';

const UNIT = 'pusat-karir';

// Kartu pimpinan cadangan — dipakai bila belum ada anggota unit ber-"Kepala Unit"
// yang dikelola dari panel admin Struktur Unit.
const HEAD_FALLBACK = {
  name: 'Nurhayati, M.Sc',
  position: 'Ka. Unit Pusat Karir & Tracer Study',
  slug: 'nurhayati-m-sc',
  photoId: 'd39d6007-a1b6-4baa-8ca0-ea90479236f4',
};

const services = [
  {
    icon: Compass,
    title: 'Bimbingan & Konseling Karir',
    description: 'Pendampingan perencanaan karir, penyusunan CV, dan persiapan menghadapi wawancara kerja.',
  },
  {
    icon: Briefcase,
    title: 'Informasi Lowongan & Magang',
    description: 'Menyalurkan informasi lowongan kerja dan kesempatan magang dari mitra rumah sakit, apotek, dan instansi kesehatan.',
  },
  {
    icon: GraduationCap,
    title: 'Pelatihan Kesiapan Kerja',
    description: 'Pelatihan public speaking, personal branding, dan keterampilan kerja lain sebagai bekal transisi ke dunia profesional.',
  },
  {
    icon: Users,
    title: 'Rekrutmen Kampus & Job Fair',
    description: 'Memfasilitasi campus hiring, bursa kerja, dan kunjungan perekrut ke kampus untuk mempertemukan lulusan dengan pemberi kerja.',
  },
  {
    icon: BarChart2,
    title: 'Tracer Study Alumni',
    description: 'Menelusuri rekam jejak lulusan sebagai dasar peningkatan mutu pembelajaran dan bukti kinerja lulusan untuk akreditasi.',
  },
  {
    icon: Handshake,
    title: 'Kemitraan Dunia Usaha & Industri',
    description: 'Membangun dan merawat kerja sama dengan DUDI untuk perluasan serapan kerja dan program magang bersertifikat.',
  },
];

const FALLBACK_STATS = [
  { val: '87%', label: 'Keterserapan Kerja', desc: 'Lulusan terserap <6 bulan setelah wisuda', icon: <Briefcase size={20} /> },
  { val: '3.8 bln', label: 'Rata-rata Waktu Tunggu', desc: 'Memperoleh pekerjaan pertama', icon: <TrendingUp size={20} /> },
  { val: '92%', label: 'Bekerja Sesuai Bidang', desc: 'Sesuai bidang ilmu kesehatan', icon: <GraduationCap size={20} /> },
  { val: '—', label: 'Responden Tracer Study', desc: 'Data alumni terkumpul', icon: <Users size={20} /> },
];

/// Menarik foto profil anggota unit sekaligus menentukan jenis tautan profil.
function useProfilePhoto(slug, profileType, { width = 240, height = 300 } = {}) {
  const [photo, setPhoto] = useState(null);
  const [linkPath, setLinkPath] = useState(null);

  useEffect(() => {
    if (!slug) return undefined;
    let active = true;
    (async () => {
      try {
        const result = await fetchUnitMemberProfile(slug, profileType);
        if (active && result) {
          setLinkPath(`/${result.kind}/${slug}`);
          if (result.profile.foto) setPhoto(getImageUrl(result.profile.foto, { width, height, quality: 85 }));
        }
      } catch {
        // Biarkan tanpa foto; kartu tetap tampil dengan inisial.
      }
    })();
    return () => { active = false; };
  }, [slug, profileType, width, height]);

  return { photo, linkPath };
}

function MemberCard({ member }) {
  const { photo, linkPath } = useProfilePhoto(member.profileSlug, member.profileType, { width: 240, height: 300 });
  const initials = member.name.split(/\s+/).slice(0, 2).map((p) => p[0]).join('').toUpperCase();

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      className={`rounded-2xl border bg-white p-6 shadow-sm transition hover:shadow-lg dark:bg-[#1A182E] ${
        member.isHead
          ? 'border-amber-300 border-l-4 border-l-amber-500 dark:border-amber-400/30 dark:border-l-amber-400'
          : 'border-gray-100 dark:border-gray-800'
      }`}
    >
      <div className="flex items-start gap-4">
        <div className="grid h-20 w-16 flex-shrink-0 place-items-center overflow-hidden rounded-xl bg-purple-50 dark:bg-purple-900/30">
          {photo
            ? <img src={photo} alt={member.name} loading="lazy" className="h-full w-full object-cover" />
            : <span className="text-lg font-bold text-[#7B2FBE]">{initials}</span>}
        </div>
        <div className="min-w-0 flex-1">
          {member.isHead && (
            <span className="mb-1.5 inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-400 px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wide text-amber-950 shadow-sm dark:border-amber-300/40 dark:bg-amber-300 dark:text-amber-950">
              <Crown size={10} /> Kepala Unit
            </span>
          )}
          <h3 className="font-heading text-sm font-bold leading-tight text-[#1A1A2E] dark:text-white">{member.name}</h3>
          <p className="mt-0.5 text-xs text-[#7B2FBE] dark:text-purple-300">{member.position}</p>
          {member.tasks?.length > 0 && (
            <ul className="mt-3 space-y-1.5">
              {member.tasks.map((task, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                  <ArrowRight size={12} className="mt-0.5 flex-shrink-0 text-[#7B2FBE]" />
                  {task}
                </li>
              ))}
            </ul>
          )}
          {linkPath && member.profileSlug && (
            <Link to={linkPath} className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#7B2FBE] hover:underline">
              <UserRound size={12} /> Lihat profil
            </Link>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function AnnouncementCard({ item }) {
  const thumbUrl = getImageUrl(item.thumbnail, { width: 640, height: 320 });
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition hover:shadow-lg dark:border-gray-800 dark:bg-[#1A182E]"
    >
      {thumbUrl && (
        <Link to={`/berita/${item.slug}`} className="block h-40 overflow-hidden">
          <img
            src={thumbUrl}
            alt={item.judul}
            loading="lazy"
            style={{ objectPosition: item.thumbnailPosition || '50% 50%' }}
            className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
          />
        </Link>
      )}
      <div className="p-6">
        <div className="mb-2 flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
          <CalendarDays size={12} /> {formatTanggalIndonesia(item.tanggal_publikasi)}
        </div>
        <Link to={`/berita/${item.slug}`} className="group block">
          <h3 className="font-heading text-sm font-bold leading-snug text-[#1A1A2E] transition-colors group-hover:text-[#7B2FBE] dark:text-white">
            {item.judul}
          </h3>
        </Link>
        {item.Excerpt && (
          <p className="mt-2 line-clamp-3 text-xs leading-relaxed text-gray-600 dark:text-gray-400">{item.Excerpt}</p>
        )}
        <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-gray-100 pt-3 dark:border-gray-800">
          <Link to={`/berita/${item.slug}`} className="inline-flex items-center gap-1.5 text-xs font-medium text-[#7B2FBE] hover:underline">
            Baca selengkapnya <ArrowRight size={12} />
          </Link>
          {item.lampiranUrl && (
            <a href={item.lampiranUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-[#7B2FBE] dark:text-gray-400">
              <Paperclip size={12} /> {item.lampiranNama || 'Lampiran'}
            </a>
          )}
          {item.tautan && (
            <a href={item.tautan} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-[#7B2FBE] dark:text-gray-400">
              <ExternalLink size={12} /> Tautan terkait
            </a>
          )}
        </div>
      </div>
    </motion.div>
  );
}

const PusatKarir = () => {
  const [members, setMembers] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [headProfile, setHeadProfile] = useState(HEAD_FALLBACK);
  const [stats, setStats] = useState(FALLBACK_STATS);
  const [stories, setStories] = useState([]);

  const headMember = members.find((m) => m.isHead) || null;
  const otherMembers = members.filter((m) => !m.isHead);

  useEffect(() => {
    let active = true;

    Promise.all([
      fetchUnitAnggota(UNIT).catch(() => []),
      fetchPengumumanUnit(UNIT).catch(() => []),
    ]).then(([memberData, announcementData]) => {
      if (!active) return;
      setMembers(Array.isArray(memberData) ? memberData : []);
      setAnnouncements(Array.isArray(announcementData) ? announcementData : []);
      setLoading(false);
    });

    // Kartu pimpinan cadangan: segarkan nama/foto dari profil dosen.
    fetchDosenByIdentifier(HEAD_FALLBACK.slug)
      .then((dosen) => {
        if (!active || !dosen) return;
        setHeadProfile((cur) => ({
          ...cur,
          name: dosen.nama || cur.name,
          slug: dosen.slug || cur.slug,
          photoId: dosen.foto || cur.photoId,
        }));
      })
      .catch(() => {});

    fetchTracerStats()
      .then((res) => {
        if (!active || !res?.ok || !res.stats) return;
        const s = res.stats;
        setStats([
          { val: s.serapanKerjaPct || '87%', label: 'Keterserapan Kerja', desc: 'Lulusan terserap <6 bulan setelah wisuda', icon: <Briefcase size={20} /> },
          { val: s.waktuTungguRataRata || '3.8 bln', label: 'Rata-rata Waktu Tunggu', desc: 'Memperoleh pekerjaan pertama', icon: <TrendingUp size={20} /> },
          { val: s.kesesuaianBidangPct || '92%', label: 'Bekerja Sesuai Bidang', desc: 'Sesuai bidang ilmu kesehatan', icon: <GraduationCap size={20} /> },
          { val: s.total ? String(s.total) : '—', label: 'Responden Tracer Study', desc: 'Data alumni terkumpul', icon: <Users size={20} /> },
        ]);
      })
      .catch(() => {});

    fetchTracerStories()
      .then((res) => {
        if (!active || !res?.ok || !res.stories?.length) return;
        setStories(res.stories.slice(0, 3));
      })
      .catch(() => {});

    return () => { active = false; };
  }, []);

  // Data kartu pimpinan: anggota ber-"Kepala Unit" bila ada, jika tidak pakai cadangan.
  const headName = headMember?.name || headProfile.name;
  const headPosition = headMember?.position || headProfile.position;
  const headTasks = headMember?.tasks || [];
  const headSlug = headMember?.profileSlug || headProfile.slug;
  const headPhoto = headMember
    ? null
    : (headProfile.photoId ? getImageUrl(headProfile.photoId, { width: 720, height: 900, quality: 88 }) : null);
  const fallbackHeadPhoto = useProfilePhoto(headMember?.profileSlug, headMember?.profileType, { width: 720, height: 900 });
  const resolvedHeadPhoto = headPhoto || fallbackHeadPhoto.photo;
  const headLink = headMember ? (fallbackHeadPhoto.linkPath || `/${headMember.profileType || 'tendik'}/${headSlug}`) : `/dosen/${headSlug}`;

  return (
  <div data-testid="pusat-karir-page">
    <SEO
      title="Pusat Karir & Tracer Study"
      description="Unit Pusat Karir & Tracer Study STIKES Harapan Ibu Jambi — bimbingan karir, informasi lowongan & magang, rekrutmen kampus, kemitraan industri, dan penelusuran alumni."
      image={resolvedHeadPhoto}
    />

    <div className="page-hero pt-[70px]">
      <div className="relative z-10 mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="mb-4 flex flex-wrap items-center gap-2 text-sm text-white/70">
            <Link to="/" className="flex items-center gap-1 transition-colors hover:text-white">
              <Home size={14} /> Beranda
            </Link>
            <ChevronRight size={14} />
            <span>Unit</span>
            <ChevronRight size={14} />
            <span className="text-white">Pusat Karir &amp; Tracer Study</span>
          </div>
          <h1 className="font-heading text-4xl font-bold text-white md:text-5xl">Pusat Karir &amp; Tracer Study</h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/80">
            Menjembatani lulusan STIKES Harapan Ibu Jambi menuju dunia kerja — melalui bimbingan karir,
            informasi lowongan, kemitraan industri, dan penelusuran alumni (tracer study).
          </p>
        </motion.div>
      </div>
    </div>

    <main className="bg-[#F8F7FF] dark:bg-[#121020]">
      {/* Tentang & Pimpinan */}
      <section className="py-16">
        <div className="mx-auto grid max-w-6xl gap-8 px-4 sm:px-6 lg:grid-cols-[360px_minmax(0,1fr)] lg:px-8">
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="overflow-hidden rounded-3xl border border-purple-100 bg-white shadow-sm dark:border-purple-900/30 dark:bg-[#1A182E]"
          >
            <div className="relative aspect-[4/5] overflow-hidden bg-purple-100 dark:bg-purple-950/30">
              {resolvedHeadPhoto ? (
                <img src={resolvedHeadPhoto} alt={headName} className="h-full w-full object-cover object-top" />
              ) : (
                <div className="grid h-full w-full place-items-center bg-gradient-to-br from-[#7B2FBE] to-[#4C1D95] text-6xl font-bold text-white">
                  {headName.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('')}
                </div>
              )}
              <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#171128]/90 to-transparent" />
              <span className="absolute bottom-4 left-4 inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-400 px-3 py-1.5 text-xs font-extrabold text-amber-950 shadow-lg shadow-black/20">
                <Crown size={13} /> Kepala Unit
              </span>
            </div>
            <div className="p-6">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#7B2FBE] dark:text-purple-300">
                Pimpinan Unit
              </p>
              <h2 className="mt-2 font-heading text-xl font-bold leading-snug text-[#1A1A2E] dark:text-white">
                {headName}
              </h2>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{headPosition}</p>
              {headTasks.length > 0 && (
                <ul className="mt-3 space-y-1.5">
                  {headTasks.map((task, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <ArrowRight size={12} className="mt-0.5 flex-shrink-0 text-[#7B2FBE]" />
                      {task}
                    </li>
                  ))}
                </ul>
              )}
              {headSlug && (
                <Link
                  to={headLink}
                  className="mt-5 inline-flex items-center gap-2 rounded-xl border border-purple-200 px-4 py-2.5 text-sm font-semibold text-[#7B2FBE] transition hover:border-[#7B2FBE] hover:bg-purple-50 dark:border-purple-800 dark:text-purple-300 dark:hover:bg-purple-900/20"
                >
                  Lihat Profil <ArrowRight size={16} />
                </Link>
              )}
            </div>
          </motion.article>

          <div className="flex flex-col justify-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7B2FBE] dark:text-purple-300">
                Unit &amp; Lembaga
              </span>
              <h2 className="mt-3 font-heading text-3xl font-bold text-[#1A1A2E] dark:text-white">
                Unit Pusat Karir &amp; Tracer Study
              </h2>
              <p className="mt-5 leading-relaxed text-gray-600 dark:text-gray-300">
                Unit Pusat Karir &amp; Tracer Study bertugas menyiapkan mahasiswa dan lulusan STIKES Harapan Ibu
                Jambi memasuki dunia kerja. Kegiatannya mencakup bimbingan karir, penyebaran informasi lowongan
                dan magang, pelatihan kesiapan kerja, penyelenggaraan rekrutmen kampus, serta menjalin kemitraan
                dengan dunia usaha dan dunia industri.
              </p>
              <p className="mt-4 leading-relaxed text-gray-600 dark:text-gray-300">
                Unit ini juga menjalankan <strong>Tracer Study</strong> — penelusuran rekam jejak alumni yang
                menjadi dasar peningkatan mutu lulusan sekaligus bukti kinerja lulusan untuk kebutuhan akreditasi.
              </p>
            </motion.div>

            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {services.map(({ icon: Icon, title, description }, index) => (
                <motion.div
                  key={title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.12 + index * 0.06 }}
                  className="rounded-2xl border border-purple-100 bg-white p-5 dark:border-purple-900/30 dark:bg-[#1A182E]"
                >
                  <span className="grid h-10 w-10 place-items-center rounded-xl bg-purple-100 text-[#7B2FBE] dark:bg-purple-900/30 dark:text-purple-300">
                    <Icon size={19} />
                  </span>
                  <h3 className="mt-4 font-heading text-sm font-bold text-[#1A1A2E] dark:text-white">{title}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-gray-500 dark:text-gray-400">{description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Anggota unit (tampil bila sudah dikelola dari admin) */}
      {otherMembers.length > 0 && (
        <section className="bg-white py-16 dark:bg-[#100E1C]">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-2xl font-bold text-[#1A1A2E] dark:text-white">Tim &amp; Anggota Unit</h2>
            <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
              Staf Unit Pusat Karir &amp; Tracer Study beserta bidang tugas masing-masing.
            </p>
            <div className="mt-6 grid gap-5 lg:grid-cols-2">
              {otherMembers.map((member) => <MemberCard key={member.id} member={member} />)}
            </div>
          </div>
        </section>
      )}

      {/* Ringkasan Tracer Study */}
      <section className={`py-16 ${otherMembers.length > 0 ? '' : 'bg-white dark:bg-[#100E1C]'}`}>
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between"
          >
            <div>
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7B2FBE] dark:text-purple-300">
                Kinerja Lulusan
              </span>
              <h2 className="mt-3 font-heading text-3xl font-bold text-[#1A1A2E] dark:text-white">
                Ringkasan Tracer Study
              </h2>
              <p className="mt-3 max-w-2xl leading-relaxed text-gray-600 dark:text-gray-300">
                Cuplikan hasil penelusuran alumni. Data lengkap dan formulir partisipasi tersedia di halaman
                Tracer Study.
              </p>
            </div>
            <Link
              to="/tracer-study"
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-[#7B2FBE] px-5 py-3 text-sm font-bold text-white shadow-lg shadow-purple-500/25 transition hover:-translate-y-0.5 hover:bg-[#9333EA]"
            >
              Lihat Data &amp; Isi Tracer Study <ArrowRight size={16} />
            </Link>
          </motion.div>

          <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
            {stats.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="rounded-2xl border border-purple-100 bg-[#F8F7FF] p-5 dark:border-purple-900/30 dark:bg-[#1A182E]"
              >
                <span className="grid h-10 w-10 place-items-center rounded-xl bg-white text-[#7B2FBE] shadow-sm dark:bg-[#100E1C] dark:text-purple-300">
                  {s.icon}
                </span>
                <div className="mt-4 font-heading text-2xl font-bold text-[#1A1A2E] dark:text-white">{s.val}</div>
                <div className="mt-1 text-xs font-semibold text-gray-700 dark:text-gray-200">{s.label}</div>
                <div className="mt-1 text-xs leading-relaxed text-gray-500 dark:text-gray-400">{s.desc}</div>
              </motion.div>
            ))}
          </div>

          {stories.length > 0 && (
            <div className="mt-6 grid gap-4 md:grid-cols-3">
              {stories.map((story, i) => (
                <motion.figure
                  key={`${story.name}-${i}`}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="flex h-full flex-col rounded-2xl border border-purple-100 bg-[#F8F7FF] p-5 dark:border-purple-900/30 dark:bg-[#1A182E]"
                >
                  <Quote size={22} className="text-[#7B2FBE]/30" />
                  <blockquote className="mt-2 flex-1 text-sm italic leading-relaxed text-gray-600 dark:text-gray-300">
                    “{story.quote}”
                  </blockquote>
                  <figcaption className="mt-4 border-t border-purple-100 pt-3 dark:border-purple-900/30">
                    <div className="font-heading text-sm font-bold text-[#1A1A2E] dark:text-white">{story.name}</div>
                    {story.role && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {story.role}{story.company ? ` · ${story.company}` : ''}
                      </div>
                    )}
                    <div className="mt-0.5 text-xs text-[#7B2FBE] dark:text-purple-300">
                      {story.program}{story.year ? ` · Alumni ${story.year}` : ''}
                    </div>
                  </figcaption>
                </motion.figure>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Pengumuman unit — dikelola dari panel admin (menu Pengumuman, unit Pusat Karir & Tracer Study) */}
      <section className="bg-white py-16 dark:bg-[#100E1C]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mb-6 flex items-end justify-between gap-4">
            <div>
              <h2 className="font-heading text-2xl font-bold text-[#1A1A2E] dark:text-white">
                Pengumuman &amp; Info Karir
              </h2>
              <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
                Lowongan, job fair, pelatihan, dan informasi karir terbaru dari unit.
              </p>
            </div>
            <Link to="/berita" className="hidden shrink-0 items-center gap-1.5 whitespace-nowrap text-sm font-medium text-[#7B2FBE] hover:underline sm:inline-flex">
              Semua berita <ArrowRight size={14} />
            </Link>
          </div>
          {loading ? (
            <div className="grid min-h-40 place-items-center text-sm text-gray-400">Memuat…</div>
          ) : announcements.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-200 py-12 text-center dark:border-gray-700">
              <Megaphone className="mx-auto text-gray-300 dark:text-gray-600" size={36} />
              <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                Belum ada pengumuman dari Unit Pusat Karir &amp; Tracer Study.
              </p>
            </div>
          ) : (
            <div className="grid gap-5 lg:grid-cols-2">
              {announcements.map((item) => <AnnouncementCard key={item.id} item={item} />)}
            </div>
          )}
        </div>
      </section>

      {/* CTA kemitraan / lowongan */}
      <section className="py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#4C1D95] via-[#7B2FBE] to-[#9333EA] p-7 text-white shadow-xl shadow-purple-900/10 sm:p-10"
          >
            <div className="absolute -right-16 -top-20 h-64 w-64 rounded-full bg-white/10" />
            <div className="absolute -bottom-24 right-32 h-48 w-48 rounded-full bg-white/5" />
            <div className="relative z-10 flex flex-col gap-7 md:flex-row md:items-center md:justify-between">
              <div className="max-w-3xl">
                <div className="flex items-center gap-2 text-sm font-semibold text-purple-100">
                  <Handshake size={19} /> Kerja Sama &amp; Rekrutmen
                </div>
                <h2 className="mt-3 font-heading text-2xl font-bold sm:text-3xl">
                  Punya lowongan atau ingin merekrut lulusan kami?
                </h2>
                <p className="mt-3 leading-relaxed text-purple-100">
                  Instansi dan mitra industri dapat menyampaikan informasi lowongan, magang, atau rencana
                  rekrutmen kampus melalui Unit Pusat Karir &amp; Tracer Study STIKES Harapan Ibu Jambi.
                </p>
                {announcements.length === 0 && (
                  <p className="mt-3 inline-flex items-center gap-1.5 text-sm text-purple-100">
                    <Newspaper size={15} />
                    Info lowongan &amp; kegiatan karir terbaru diumumkan di
                    <Link to="/berita" className="font-semibold text-white underline underline-offset-2">
                      Berita &amp; Pengumuman
                    </Link>
                  </p>
                )}
              </div>
              <Link
                to="/kontak"
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-bold text-[#6D28A8] shadow-lg transition hover:-translate-y-0.5 hover:bg-purple-50"
              >
                Hubungi Pusat Karir <ArrowRight size={16} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  </div>
  );
};

export default PusatKarir;
